/**
 * Contact form: 3-state submit button + AJAX submission.
 *
 * Button states (via [data-state] on the button element):
 *   idle    — ready to submit
 *   sending — fetch in flight; progress fill + label slide animate
 *   success — 3D flip to back face (checkmark); click outside / Escape / click the
 *             button resets the form (no reverse flip — the bar just shrinks back)
 *   error   — morphs to warning circle; shake + error bubble appears
 *
 * Error state covers: ratelimited, expired form, mail send failure, network
 * failures and request timeouts.
 * All user-visible strings are read from data-str-* attributes on the
 * <form> element — resolved by PHP from the $td_cf_strings config array, so
 * multilang logic stays entirely in PHP.
 *
 * If JS fails to load entirely the form still works as a plain POST
 * (server-side no-JS fallback is in place in function-contact-form.php).
 */
( function () {
    'use strict';

    /**
     * Minimum time (ms) the sending animation plays regardless of server
     * response speed. If the server responds faster than this, we wait for
     * the remainder. If slower, we simply wait. This is a floor, not a
     * fixed delay — fast real-world responses still benefit from the
     * animation without artificial lag on slow connections.
     *
     * Adjust per site here or expose to the config PHP file if needed.
     */
    var MIN_ANIMATION_MS = 1400;

    /**
     * Give up on a request that gets no answer after this long (ms), so the
     * button can never sit at 100% indefinitely. Keep it above the SMTP
     * timeout in config-01-smtp.php ($td_cf_smtp_timeout, 15 s).
     */
    var REQUEST_TIMEOUT_MS = 20000;

    /* ================================================================
       Boot — runs whether script loads before or after DOMContentLoaded
       ================================================================ */

    function boot() {
        document.querySelectorAll( '[data-cform]' ).forEach( initForm );
    }

    if ( 'loading' === document.readyState ) {
        document.addEventListener( 'DOMContentLoaded', boot );
    } else {
        boot();
    }

    /* ================================================================
       initForm — called once per [data-cform] wrapper on the page
       ================================================================ */

    function initForm( cform ) {
        var form           = cform.querySelector( '[data-cform-form]' );
        var fields         = Array.from( cform.querySelectorAll( '[data-cform-field]' ) );
        var submitBtn      = cform.querySelector( '[data-cform-submit]' );
        var actions        = cform.querySelector( '.cform__actions' );

        if ( ! form || ! submitBtn || ! actions ) { return; }

        /* ---- Read multilang strings from PHP-rendered data attributes ---- *
         * Fallback values are last-resort English strings in case a data     *
         * attribute is accidentally missing — PHP should always provide them. */

        var strings = {
            send:             form.dataset.strSend             || 'Send',
            sending:          form.dataset.strSending          || 'Sending…',
            sent:             form.dataset.strSent             || 'Sent',
            errorTitle:       form.dataset.strErrorTitle       || 'Not sent.',
            errorRetry:       form.dataset.strErrorRetry       || 'Please try again later.',
            errorRatelimited: form.dataset.strErrorRatelimited || 'Too many attempts.',
            errorMail:        form.dataset.strErrorMail        || 'Sending failed.',
            errorExpired:     form.dataset.strErrorExpired     || 'The form has expired. Please refresh the page.',
            errorDismiss:     form.dataset.strErrorDismiss     || 'close',
            fieldRequired:    form.dataset.strFieldRequired    || 'This field is required.',
            fieldEmail:       form.dataset.strFieldEmail       || 'Please enter a valid email address.',
        };

        /* ---- Enhance button with 3D faces + warning icon -----------------
         * Button internals are injected by JS (not in PHP) to keep the PHP
         * markup clean and allow the no-JS button to be just a plain button. */

        // Live region: rendered by PHP next to the button wrap (NOT inside the
        // button — a button's children are presentational for assistive tech).
        var statusEl = actions.querySelector( '[data-cform-status]' );

        var frontFace = el( 'span',
            { class: 'cform__submit__face cform__submit__face--front', 'aria-hidden': 'true' },
            el( 'span', { class: 'cform__submit__progress', 'data-progress': '' } ),
            el( 'span', { class: 'cform__submit__label' },
                el( 'span', { class: 'cform__submit__text cform__submit__text--idle' },    strings.send ),
                el( 'span', { class: 'cform__submit__text cform__submit__text--sending' }, strings.sending )
            )
        );

        var backFace = el( 'span',
            { class: 'cform__submit__face cform__submit__face--back', 'aria-hidden': 'true' },
            el( 'span', { class: 'cform__submit__sent' },
                svgCheck(),
                el( 'span', {}, strings.sent )
            )
        );

        var iconEl = el( 'span', { class: 'cform__submit__icon', 'aria-hidden': 'true' }, svgWarning() );

        // Insert faces and icon.
        submitBtn.append( frontFace, backFace, iconEl );

        // Faces now carry the button colours; CSS stops painting the bare
        // button background (which is the no-JS / pre-JS look).
        submitBtn.classList.add( 'cform__submit--enhanced' );

        var progress = frontFace.querySelector( '[data-progress]' );

        /* ---- Send error bubble -------------------------------------------
         * Injected before the button wrap so it sits above the button in
         * the no-JS fallback document flow (and is anchored there via CSS). */

        var sendErrorBubble = el( 'div',
            { class: 'cform__send-error', role: 'alert' }, // role=alert already implies assertive
            el( 'p', { class: 'cform__send-error__title' }, strings.errorTitle ),
            el( 'p', { class: 'cform__send-error__detail', 'data-send-detail': '' } ),
            el( 'button', {
                type:        'button',
                class:       'cform__bubble-dismiss',
                'aria-label': strings.errorDismiss,
                'data-send-dismiss': ''
            }, svgClose() )
        );

        var sendDetail  = sendErrorBubble.querySelector( '[data-send-detail]' );
        var sendDismiss = sendErrorBubble.querySelector( '[data-send-dismiss]' );

        var wrap = submitBtn.closest( '.cform__submit-wrap' );
        actions.insertBefore( sendErrorBubble, wrap );

        /* ================================================================
           State machine
           ================================================================ */

        var state = 'idle';

        // Fields are locked while the "sent" state shows, and cleared when the
        // visitor dismisses it. inert keeps them out of the tab order as well.
        function lockFields( locked ) {
            fields.forEach( function ( input ) {
                var field = input.closest( '.cform__field' );
                if ( ! field ) { return; }
                if ( locked ) { field.setAttribute( 'inert', '' ); }
                else          { field.removeAttribute( 'inert' ); }
            } );
        }

        function resetFields() {
            lockFields( false );
            fields.forEach( function ( input ) {
                input.value = '';
                clearError( input );
            } );
            hasAttemptedSubmit = false;
        }

        function setState( next, errorType ) {
            var previous = state;

            // Leaving success: no reverse flip (see .is-resetting in the SCSS). The class
            // must be present for the same style change as data-state, so it goes on first.
            if ( previous === 'success' && next === 'idle' ) {
                submitBtn.classList.add( 'is-resetting' );
                requestAnimationFrame( function () {
                    requestAnimationFrame( function () {
                        submitBtn.classList.remove( 'is-resetting' );
                    } );
                } );
            }

            state = next;
            submitBtn.setAttribute( 'data-state', next );

            var isDisabled = next === 'sending' || next === 'success' || next === 'error';
            // aria-disabled instead of the disabled attribute: a disabled button
            // drops keyboard focus. Clicks/Enter are ignored in the submit handler.
            submitBtn.setAttribute( 'aria-disabled', String( isDisabled ) );

            if ( next === 'idle' ) {
                sendErrorBubble.classList.remove( 'is-visible' );
                if ( previous === 'success' ) { resetFields(); } // blank form for the next message
                progress.style.transition = 'none';
                progress.style.width      = '0%';
                statusEl.textContent      = '';
            }

            if ( next === 'sending' ) {
                statusEl.textContent = strings.sending;

                /*
                 * void progress.offsetWidth forces a synchronous repaint
                 * between the two style assignments. Without it both collapse
                 * into one recalc — the browser sees no "before" state and
                 * the CSS transition never fires.
                 */
                progress.style.transition = 'none';
                progress.style.width      = '0%';
                void progress.offsetWidth;
                progress.style.transition = 'width ' + MIN_ANIMATION_MS + 'ms linear';
                progress.style.width      = '100%';
            }

            if ( next === 'success' ) {
                statusEl.textContent = strings.sent;

                // Keep the sent message from being edited while the green state shows.
                lockFields( true );
            }

            if ( next === 'error' ) {
                progress.style.transition = 'none';
                progress.style.width      = '0%';

                var errorMessages = {
                    'ratelimited': strings.errorRatelimited,
                    'mail_error':  strings.errorMail,
                    'expired':     strings.errorExpired,
                };
                var detail = errorMessages[ errorType ] || strings.errorRetry;

                sendDetail.textContent = detail;
                statusEl.textContent   = ''; // the role=alert bubble announces the error; don't say it twice
                sendErrorBubble.classList.add( 'is-visible' );
            }
        }

        /* ---- Dismiss send error ------------------------------------------ */

        sendDismiss.addEventListener( 'click', function () {
            setState( 'idle' );
        } );

        // Light-dismiss for the error and success states: any click outside the
        // button. A click on the button itself is handled by the submit handler
        // (it also resets), so excluding it here avoids dismissing and then
        // immediately re-submitting. The error bubble's own × has its own handler.
        document.addEventListener( 'click', function ( e ) {
            if ( state !== 'error' && state !== 'success' ) { return; }
            if ( submitBtn.contains( e.target ) ) { return; }
            if ( state === 'error' && sendErrorBubble.contains( e.target ) ) { return; }
            setState( 'idle' );
        }, true );

        // Escape: keyboard equivalent of the light-dismiss above
        document.addEventListener( 'keydown', function ( e ) {
            if ( 'Escape' === e.key && ( state === 'error' || state === 'success' ) ) {
                setState( 'idle' );
            }
        } );

        /* ================================================================
           Validation
           ================================================================ */

        var hasAttemptedSubmit = false;

        function errorFor( input ) {
            if ( ! input.validity.valid ) {
                if ( input.validity.valueMissing ) { return strings.fieldRequired; }
                if ( input.validity.typeMismatch ) { return strings.fieldEmail; }
                return input.validationMessage; // browser's native message for other cases
            }
            return null;
        }

        function markError( input, message ) {
            var field   = input.closest( '.cform__field' );
            var errorEl = field ? field.querySelector( '[data-cform-error]' ) : null;
            if ( field )   { field.classList.add( 'cform__field--has-error' ); }
            if ( errorEl ) { errorEl.textContent = message; errorEl.removeAttribute( 'hidden' ); }
        }

        function clearError( input ) {
            var field   = input.closest( '.cform__field' );
            var errorEl = field ? field.querySelector( '[data-cform-error]' ) : null;
            if ( field )   { field.classList.remove( 'cform__field--has-error' ); }
            if ( errorEl ) { errorEl.textContent = ''; errorEl.setAttribute( 'hidden', '' ); }
        }

        function validateAll() {
            var first = null;
            fields.forEach( function ( input ) {
                var msg = errorFor( input );
                if ( msg ) { markError( input, msg ); if ( ! first ) { first = input; } }
                else       { clearError( input ); }
            } );
            return first; // first invalid input, or null if all valid
        }

        function applyServerErrors( errors ) {
            var first = null;
            Object.keys( errors ).forEach( function ( fieldName ) {
                var input = form.querySelector( '[name="' + fieldName + '"]' );
                if ( input ) {
                    markError( input, errors[ fieldName ] );
                    if ( ! first ) { first = input; }
                }
            } );
            if ( first ) { first.focus(); }
        }

        function shake() {
            submitBtn.classList.remove( 'is-shaking' );
            void submitBtn.offsetWidth; // force reflow so animation can retrigger
            submitBtn.classList.add( 'is-shaking' );
            submitBtn.addEventListener( 'animationend', function () {
                submitBtn.classList.remove( 'is-shaking' );
            }, { once: true } );
        }

        // clear per-field error as user corrects it (only after first submit attempt)
        fields.forEach( function ( input ) {
            input.addEventListener( 'input', function () {
                if ( ! hasAttemptedSubmit ) { return; }
                var msg = errorFor( input );
                if ( msg ) { markError( input, msg ); }
                else       { clearError( input ); }
            } );
        } );

        /* ================================================================
           Fetch
           ================================================================ */

        function sendRequest() {
            // Abort a stalled request instead of leaving the button at 100% forever.
            var controller = 'AbortController' in window ? new AbortController() : null;
            var timer      = controller
                ? setTimeout( function () { controller.abort(); }, REQUEST_TIMEOUT_MS )
                : null;

            return fetch( form.getAttribute( 'action' ), {
                method:      'POST',
                body:        new FormData( form ),
                credentials: 'same-origin',
                headers:     { 'X-Requested-With': 'XMLHttpRequest' },
                signal:      controller ? controller.signal : undefined
            } )
            .then( function ( response ) {
                // Non-2xx HTTP status (e.g. 500) — treat as a send failure
                if ( ! response.ok ) { throw { type: 'mail_error' }; }
                return response.json();
            } )
            .then( function ( json ) {
                var data   = json && json.data ? json.data : {};
                var status = data.status;

                if ( status === 'success' )               { return { type: 'success' }; }
                if ( status === 'error' && data.errors )  { return { type: 'validation', errors: data.errors }; }

                // ratelimited, expired, mail_error, or unexpected status
                return { type: status || 'mail_error' };
            } )
            .then( function ( result ) {
                clearTimeout( timer );
                return result;
            }, function ( err ) {
                clearTimeout( timer );
                throw ( err && 'AbortError' === err.name ) ? { type: 'timeout' } : err;
            } );
        }

        /* ================================================================
           Run send
           ================================================================ */

        function runSend() {
            setState( 'sending' );
            var startedAt = Date.now();

            sendRequest()
                .then( function ( result ) {
                    var wait = Math.max( MIN_ANIMATION_MS - ( Date.now() - startedAt ), 0 );
                    setTimeout( function () {
                        if ( result.type === 'success' ) {
                            setState( 'success' );
                        } else if ( result.type === 'validation' ) {
                            setState( 'idle' );
                            applyServerErrors( result.errors );
                        } else {
                            setState( 'error', result.type );
                        }
                    }, wait );
                } )
                [ 'catch' ]( function ( err ) {
                    // Genuine network error or non-JSON response
                    console.error( 'Contact form: request failed.', err );
                    var wait = Math.max( MIN_ANIMATION_MS - ( Date.now() - startedAt ), 0 );
                    setTimeout( function () {
                        setState( 'error', ( err && err.type ) || 'mail_error' );
                    }, wait );
                } );
        }

        /* ================================================================
           Submit handler
           ================================================================ */

        form.addEventListener( 'submit', function ( e ) {
            e.preventDefault();
            if ( state === 'error' || state === 'success' ) { setState( 'idle' ); return; } // activating the red/green button resets it
            if ( state !== 'idle' ) { return; }

            hasAttemptedSubmit = true;
            var firstError = validateAll();

            if ( firstError ) {
                shake();
                firstError.focus();
                return;
            }

            runSend();
        } );
    }

    /* ================================================================
       Helpers
       ================================================================ */

    /**
     * Create an element with attributes and child nodes/strings.
     * Usage: el('div', { class: 'foo' }, 'text', childEl)
     */
    function el( tag, attrs ) {
        var node = document.createElement( tag );
        Object.keys( attrs ).forEach( function ( k ) { node.setAttribute( k, attrs[ k ] ); } );
        for ( var i = 2; i < arguments.length; i++ ) {
            var child = arguments[ i ];
            node.appendChild(
                typeof child === 'string'
                    ? document.createTextNode( child )
                    : child
            );
        }
        return node;
    }

    function svgCheck() {
        var s = document.createElementNS( 'http://www.w3.org/2000/svg', 'svg' );
        s.setAttribute( 'class',       'cform__submit__check' );
        // width/height attributes are required, not decoration: a common global reset,
        // svg:not([width]) { width: 100% }, outranks the class selector in the SCSS
        // and would stretch this icon across the whole button. With the attribute
        // present that selector no longer matches. (The other two icons already have them.)
        s.setAttribute( 'width',       '1em' );
        s.setAttribute( 'height',      '1em' );
        s.setAttribute( 'viewBox',     '0 0 24 24' );
        s.setAttribute( 'aria-hidden', 'true' );
        var p = document.createElementNS( 'http://www.w3.org/2000/svg', 'path' );
        p.setAttribute( 'd', 'M4 12.5l5 5L20 6.5' );
        p.setAttribute( 'pathLength', '1' ); // CSS dasharray/dashoffset = 1 (real length is ~22.6)
        s.appendChild( p );
        return s;
    }

    function svgWarning() {
        var s = document.createElementNS( 'http://www.w3.org/2000/svg', 'svg' );
        s.setAttribute( 'width',           '1.2em' );
        s.setAttribute( 'height',          '1.2em' );
        s.setAttribute( 'viewBox',         '0 0 24 24' );
        s.setAttribute( 'fill',            'none' );
        s.setAttribute( 'stroke',          'currentColor' );
        s.setAttribute( 'stroke-width',    '2.5' );
        s.setAttribute( 'stroke-linecap',  'round' );
        s.setAttribute( 'stroke-linejoin', 'round' );
        s.setAttribute( 'aria-hidden',     'true' );

        var path = document.createElementNS( 'http://www.w3.org/2000/svg', 'path' );
        path.setAttribute( 'd', 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' );
        s.appendChild( path );

        var line1 = document.createElementNS( 'http://www.w3.org/2000/svg', 'line' );
        line1.setAttribute( 'x1', '12' ); line1.setAttribute( 'y1', '9' );
        line1.setAttribute( 'x2', '12' ); line1.setAttribute( 'y2', '13' );
        s.appendChild( line1 );

        var line2 = document.createElementNS( 'http://www.w3.org/2000/svg', 'line' );
        line2.setAttribute( 'x1', '12' );    line2.setAttribute( 'y1', '17' );
        line2.setAttribute( 'x2', '12.01' ); line2.setAttribute( 'y2', '17' );
        s.appendChild( line2 );

        return s;
    }

    function svgClose() {
        var s = document.createElementNS( 'http://www.w3.org/2000/svg', 'svg' );
        s.setAttribute( 'width',       '0.75em' );
        s.setAttribute( 'height',      '0.75em' );
        s.setAttribute( 'viewBox',     '0 0 12 12' );
        s.setAttribute( 'aria-hidden', 'true' );
        var p = document.createElementNS( 'http://www.w3.org/2000/svg', 'path' );
        p.setAttribute( 'd',             'M1 1l10 10M11 1L1 11' );
        p.setAttribute( 'stroke',        'currentColor' );
        p.setAttribute( 'stroke-width',  '2' );
        p.setAttribute( 'stroke-linecap', 'round' );
        s.appendChild( p );
        return s;
    }

}() );
